import React from "react";
import { useRouter } from "next/router";
import Cookies from "js-cookie";
import { createFlagsmithInstance } from "@flagsmith/flagsmith/isomorphic";
import { FlagsmithProvider } from "@flagsmith/flagsmith/react";

import { FeatureFlagsBootstrap } from "@/interfaces/IFeatureFlags";
import { getFeatureFlagsBootstrap } from "@/services/feature-flags";
import { getPublicEnv } from "@/runtime-utils/config/public-env";
import { RELEASE_FLAG_KEYS } from "@/config/releaseFlags";

type FeatureFlagValue = string | number | boolean | null;

type GetFlagValue = (...args: [string, FeatureFlagValue?]) => FeatureFlagValue;

type IsFlagEnabled = (...args: [string, boolean?]) => boolean;

interface FeatureFlagsContextValue {
  bootstrap: FeatureFlagsBootstrap | null;
  flags: FeatureFlagsBootstrap["flags"];
  getValue: GetFlagValue;
  isEnabled: IsFlagEnabled;
  initialized: boolean;
  loading: boolean;
  refreshFlags: () => Promise<FeatureFlagsBootstrap | null>;
}

const FeatureFlagsContext = React.createContext<FeatureFlagsContextValue>({
  bootstrap: null,
  flags: {},
  getValue: (...args) => args[1] ?? null,
  isEnabled: (...args) => args[1] ?? false,
  initialized: false,
  loading: false,
  refreshFlags: async () => null,
});

const FEATURE_FLAG_CACHE_PREFIX = "wisterias.feature-flags";

type CachedFlags = FeatureFlagsBootstrap["flags"];

const DEFAULT_FLAGS: CachedFlags = Object.fromEntries(
  RELEASE_FLAG_KEYS.map((key) => [
    key,
    { enabled: false, value: null, source: "default" },
  ])
);

const getFeatureFlagCacheKey = (tenant: string) =>
  `${FEATURE_FLAG_CACHE_PREFIX}:${tenant}`;

const readCachedFlags = (tenant: string): CachedFlags => {
  try {
    const cached = window.localStorage.getItem(getFeatureFlagCacheKey(tenant));
    return cached ? { ...DEFAULT_FLAGS, ...JSON.parse(cached) } : DEFAULT_FLAGS;
  } catch {
    return DEFAULT_FLAGS;
  }
};

const saveCachedFlags = (tenant: string, flags: CachedFlags) => {
  try {
    window.localStorage.setItem(
      getFeatureFlagCacheKey(tenant),
      JSON.stringify(flags)
    );
  } catch {
    // A private browser context can deny local storage; safe defaults still apply.
  }
};

export function FeatureFlagsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const [bootstrap, setBootstrap] =
    React.useState<FeatureFlagsBootstrap | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [initialized, setInitialized] = React.useState(false);
  const [, setFlagsmithRevision] = React.useState(0);
  const [cachedFlags, setCachedFlags] = React.useState<CachedFlags>(() => {
    if (typeof window === "undefined") {
      return DEFAULT_FLAGS;
    }

    const tenant =
      Cookies.get("tenant") ?? window.localStorage.getItem("tenant");
    return tenant ? readCachedFlags(tenant) : DEFAULT_FLAGS;
  });
  const lastSessionRef = React.useRef<string | null>(null);
  const [flagsmith] = React.useState(() => createFlagsmithInstance());
  const flagsmithInitialisedRef = React.useRef(false);

  const refreshFlags = React.useCallback(async () => {
    if (typeof window === "undefined") {
      return null;
    }

    const token = Cookies.get("token");
    const tenant =
      Cookies.get("tenant") ?? window.localStorage.getItem("tenant");

    if (!token || !tenant) {
      setBootstrap(null);
      setCachedFlags(DEFAULT_FLAGS);
      lastSessionRef.current = null;
      setInitialized(true);
      return null;
    }

    const environmentID = getPublicEnv("NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY");
    const flagsmithApi = getPublicEnv("NEXT_PUBLIC_FLAGSMITH_API_URL");
    const useClientFlagsmith = Boolean(environmentID && flagsmithApi);

    setLoading(true);

    if (useClientFlagsmith) {
      try {
        if (!flagsmithInitialisedRef.current) {
          await flagsmith.init({
            environmentID,
            api: flagsmithApi,
            cacheFlags: true,
            cacheOptions: { loadStale: true },
            defaultFlags: DEFAULT_FLAGS,
            enableAnalytics:
              getPublicEnv("NEXT_PUBLIC_FLAGSMITH_ENABLE_ANALYTICS") === "true",
          });
          flagsmithInitialisedRef.current = true;
        } else {
          await flagsmith.getFlags();
        }
        const evaluatedFlags = Object.fromEntries(
          RELEASE_FLAG_KEYS.map((key) => [
            key,
            {
              enabled: flagsmith.hasFeature(key),
              value: flagsmith.getValue(key),
              source: "flagsmith",
            },
          ])
        ) as CachedFlags;
        saveCachedFlags(tenant, evaluatedFlags);
        setCachedFlags(evaluatedFlags);
        lastSessionRef.current = `${token}:${tenant}`;
      } catch {
        flagsmithInitialisedRef.current = false;
      } finally {
        setLoading(false);
        setInitialized(true);
        setFlagsmithRevision((revision) => revision + 1);
      }
      return null;
    }

    const response = await getFeatureFlagsBootstrap();
    setLoading(false);
    setInitialized(true);

    if ((response as any)?.success === false) {
      return null;
    }

    lastSessionRef.current = `${token}:${tenant}`;
    setBootstrap(response);
    flagsmith.setState(response as any);
    return response;
  }, [flagsmith]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const token = Cookies.get("token");
    const tenant =
      Cookies.get("tenant") ?? window.localStorage.getItem("tenant");

    if (!token || !tenant) {
      setBootstrap(null);
      setCachedFlags(DEFAULT_FLAGS);
      lastSessionRef.current = null;
      setInitialized(true);
      return;
    }

    const sessionKey = `${token}:${tenant}`;

    setCachedFlags(readCachedFlags(tenant));

    if (
      lastSessionRef.current === sessionKey &&
      (bootstrap || (initialized && flagsmithInitialisedRef.current))
    ) {
      return;
    }

    void refreshFlags();
  }, [bootstrap, initialized, refreshFlags, router.asPath]);

  return (
    <FlagsmithProvider flagsmith={flagsmith}>
      <FeatureFlagsContext.Provider
        value={{
          bootstrap,
          flags: bootstrap?.flags || cachedFlags,
          initialized,
          loading,
          refreshFlags,
          isEnabled: (key, fallback = false) => {
            try {
              if (flagsmithInitialisedRef.current) {
                return flagsmith.hasFeature(key);
              }
            } catch {
              // Fall back to the tenant bootstrap when the SDK is unavailable.
            }
            return (
              bootstrap?.flags?.[key]?.enabled ??
              cachedFlags[key]?.enabled ??
              fallback
            );
          },
          getValue: (key, fallback = null) => {
            try {
              if (flagsmithInitialisedRef.current) {
                return flagsmith.getValue(key) ?? fallback;
              }
            } catch {
              // Fall back to the tenant bootstrap when the SDK is unavailable.
            }
            return (
              bootstrap?.flags?.[key]?.value ??
              cachedFlags[key]?.value ??
              fallback
            );
          },
        }}
      >
        {children}
      </FeatureFlagsContext.Provider>
    </FlagsmithProvider>
  );
}

export const useFeatureFlags = () => React.useContext(FeatureFlagsContext);
